import { useEffect, useRef, useState } from "react";

const ACTION = "button, a, [role='radio']";

export interface FieldSeen {
  /** The visitor has left the field (or pressed Enter in it): its inline error may show. */
  seen: boolean;
  markSeen: () => void;
  onBlur: () => void;
}

/**
 * Whether a required field has been left, for its inline error (the wizard keeps Continue off, so the field itself must say why).
 * Leaving it by pressing a button, a link or a choice chip does not count: the error line would push that control from under the
 * pointer between the press and the release, and the click would be lost. The press itself is tracked (`pointerdown` runs before
 * the field blurs, `pointerup` ends it) because Safari and WebKit do not focus a button on a press, so the blur names nothing.
 * Tabbing to a control still counts: the keyboard has no pointer to lose.
 */
export function useFieldSeen(): FieldSeen {
  const [seen, setSeen] = useState(false);
  const pressingAction = useRef(false);
  useEffect(() => {
    const press = (event: PointerEvent) => {
      pressingAction.current = event.target instanceof Element && event.target.closest(ACTION) !== null;
    };
    const release = () => {
      pressingAction.current = false;
    };
    document.addEventListener("pointerdown", press, true);
    document.addEventListener("pointerup", release, true);
    document.addEventListener("pointercancel", release, true);
    return () => {
      document.removeEventListener("pointerdown", press, true);
      document.removeEventListener("pointerup", release, true);
      document.removeEventListener("pointercancel", release, true);
    };
  }, []);
  return {
    seen,
    markSeen: () => {
      setSeen(true);
    },
    onBlur: () => {
      if (pressingAction.current) return;
      setSeen(true);
    },
  };
}
